import React, { useState, useRef, useEffect, useCallback } from "react";
import { ShortsVideoPlayer } from "./ShortsVideoPlayer";
import { AuthenticationService } from "../../services/authService";
import {
  getShorts,
  getShortDetail,
  incrementShortView,
  likeShort,
  unlikeShort,
} from "../../services/shortsService";

const PAGE_SIZE = 12;
const TOKEN_REFRESH_MS = 25 * 60 * 1000;
const IDLE_PAUSE_MS = 10 * 60 * 1000;

const fmtViews = (n) => {
  const v = Number(n) || 0;
  if (v >= 1000000000) return (v / 1000000000).toFixed(1).replace(/\.0$/, "") + "B";
  if (v >= 1000000) return (v / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  if (v >= 1000) return (v / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  return "" + v;
};

const fmtTime = (s) => {
  const t = Math.max(0, Math.floor(Number(s) || 0));
  const m = Math.floor(t / 60);
  const r = t % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
};

const toListItem = (d) => ({
  publicId: d.publicId,
  title: d.title,
  thumbnailUrl: d.thumbnailUrl,
  durationSeconds: d.durationSeconds,
  viewCount: d.viewCount,
  likeCount: d.likeCount,
});

function Caption({ caption, t }) {
  const [open, setOpen] = useState(false);
  if (!caption) return null;
  const parts = caption.split(/(#[^\s]+)/g);
  const render = parts.map((p, i) =>
    p.startsWith("#") ? (
      <span key={i} className="dk-tag">{p}</span>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    )
  );
  const isLong = caption.length > 70;
  const short = isLong ? caption.slice(0, 68) + "…" : caption;
  return (
    <div className="dk-v-cap">
      {open || !isLong ? (
        <span>
          {render}
          {isLong && (
            <span className="dk-v-more" onClick={() => setOpen(false)}> {t.collapse}</span>
          )}
        </span>
      ) : (
        <span>
          {short}
          <span className="dk-v-more" onClick={() => setOpen(true)}> {t.seeMore}</span>
        </span>
      )}
    </div>
  );
}

function FeedItem({ item, index, active, innerRef, detail, t, muted, volume, onToggleMute, onVolume, onMutedSync, onLike, onShare, onView, onExit, onNavigate, onRefresh }) {
  const playerRef = useRef(null);
  const progRef = useRef(null);
  const draggingRef = useRef(false);
  const navRef = useRef(onNavigate);
  navRef.current = onNavigate;
  const retriedRef = useRef(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(item.durationSeconds || 0);
  const [playing, setPlaying] = useState(true);
  const [errored, setErrored] = useState(false);
  const [idlePaused, setIdlePaused] = useState(false);
  const idleTimerRef = useRef(null);

  const videoUrl = detail && detail.videoUrl;
  const poster = (detail && detail.thumbnailUrl) || item.thumbnailUrl || "";
  const liked = !!(detail && detail.likedByMe);
  const likeCount = detail ? detail.likeCount : item.likeCount;
  const viewCount = detail ? detail.viewCount : item.viewCount;
  const title = (detail && detail.title) || item.title;
  const frac = duration ? Math.min(1, time / duration) : 0;

  useEffect(() => {
    if (active) setErrored(false);
  }, [active, videoUrl]);

  useEffect(() => {
    retriedRef.current = false;
    setIdlePaused(false);
  }, [active]);

  const handleError = () => {
    if (!retriedRef.current && onRefresh) {
      retriedRef.current = true;
      onRefresh(item.publicId).catch(() => setErrored(true));
    } else {
      setErrored(true);
    }
  };

  const armIdle = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      const p = playerRef.current;
      if (p) p.pause();
      setIdlePaused(true);
    }, IDLE_PAUSE_MS);
  }, []);

  const resumeFromIdle = () => {
    setIdlePaused(false);
    if (onRefresh) onRefresh(item.publicId);
    const p = playerRef.current;
    if (p) p.play();
  };

  useEffect(() => {
    if (!active || idlePaused || !onRefresh) return;
    const id = setInterval(() => onRefresh(item.publicId), TOKEN_REFRESH_MS);
    return () => clearInterval(id);
  }, [active, idlePaused, onRefresh, item.publicId]);

  useEffect(() => {
    if (!active || idlePaused) return;
    armIdle();
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [active, idlePaused, armIdle]);

  const seekAt = useCallback((clientX) => {
    const el = progRef.current;
    const p = playerRef.current;
    if (!el || !p) return;
    const r = el.getBoundingClientRect();
    p.seekFrac((clientX - r.left) / r.width);
  }, []);

  useEffect(() => {
    if (!active) return;
    const move = (e) => draggingRef.current && seekAt(e.clientX);
    const up = () => (draggingRef.current = false);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [active, seekAt]);

  const togglePlay = () => {
    armIdle();
    playerRef.current && playerRef.current.toggle();
  };

  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || "";
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "BUTTON" || tag === "A") return;
      const p = playerRef.current;
      switch (e.code) {
        case "Space":
          e.preventDefault();
          togglePlay();
          break;
        case "Escape":
          onExit && onExit();
          break;
        case "ArrowDown":
          e.preventDefault();
          onNavigate && onNavigate(1);
          break;
        case "ArrowUp":
          e.preventDefault();
          onNavigate && onNavigate(-1);
          break;
        case "ArrowRight":
          e.preventDefault();
          p && p.seekBy(5);
          break;
        case "ArrowLeft":
          e.preventDefault();
          p && p.seekBy(-5);
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, onNavigate, onExit]);

  useEffect(() => {
    if (!active) return;
    const failed = errored || (detail && !videoUrl);
    if (!failed) return;
    const timer = setTimeout(() => navRef.current && navRef.current(1), 1200);
    return () => clearTimeout(timer);
  }, [active, errored, detail, videoUrl]);

  return (
    <div className="dk-feed-item" data-index={index} ref={innerRef}>
      <div className="dk-feed-nav">
        <button className="dk-feed-nav-btn" onClick={() => onNavigate(-1)} aria-label={t.prev}>
          <i className="pi pi-arrow-up" />
        </button>
        <button className="dk-feed-nav-btn" onClick={() => onNavigate(1)} aria-label={t.next}>
          <i className="pi pi-arrow-down" />
        </button>
      </div>

      <div className={"dk-feed-video" + (active ? " is-active" : "")}>
{active && videoUrl && !errored ? (
          <>
            <ShortsVideoPlayer
              ref={playerRef}
              src={videoUrl}
              poster={poster}
              muted={muted}
              volume={volume}
              onPlay={() => onView(item.publicId)}
              onError={handleError}
              onTime={(tm, d) => {
                setTime(tm);
                if (d) setDuration(d);
              }}
              onPlayingChange={setPlaying}
              onMutedChange={onMutedSync}
            />
            <div className="dk-feed-tap" onClick={togglePlay} />
            {!idlePaused && (
              <button
                type="button"
                className={"dk-feed-bigplay" + (playing ? " is-playing" : "")}
                onClick={togglePlay}
                aria-label={playing ? "Pause" : "Play"}
              >
                <i className={playing ? "pi pi-pause" : "pi pi-play"} />
              </button>
            )}
            {idlePaused && (
              <div className="dk-feed-idle" onClick={resumeFromIdle}>
                <button type="button" className="dk-feed-idle__btn" aria-label={t.continueWatching}>
                  <i className="pi pi-play" />
                </button>
                <p className="dk-feed-idle__text">{t.stillWatching}</p>
              </div>
            )}
          </>
        ) : (
          <div
            className="dk-feed-poster"
            style={poster ? { backgroundImage: `url(${poster})` } : undefined}
          >
            {active && !detail ? (
              <span className="dk-ring" />
            ) : active && (errored || (detail && !videoUrl)) ? (
              <span className="dk-feed-poster-msg">
                <i className="pi pi-exclamation-triangle" /> {t.videoError}
              </span>
            ) : !poster ? (
              <i className="pi pi-video" />
            ) : (
              <span className="dk-feed-poster-play"><i className="pi pi-play" /></span>
            )}
          </div>
        )}

        <div className="dk-v-rail">
          <button className={"dk-rail-btn" + (liked ? " liked" : "")} onClick={() => onLike(item.publicId)}>
            <span className="dk-rail-ic"><i className={"pi " + (liked ? "pi-heart-fill" : "pi-heart")} /></span>
            <span className="dk-rail-lab">{fmtViews(likeCount)}</span>
          </button>
          <button className="dk-rail-btn" onClick={() => onShare(item.publicId)}>
            <span className="dk-rail-ic"><i className="pi pi-share-alt" /></span>
            <span className="dk-rail-lab">{t.share}</span>
          </button>
          <button className="dk-rail-btn" onClick={onToggleMute}>
            <span className="dk-rail-ic"><i className={"pi " + (muted ? "pi-volume-off" : "pi-volume-up")} /></span>
            <span className="dk-rail-lab">{muted ? t.muted : t.sound}</span>
          </button>
        </div>

        <div className="dk-v-meta">
          <div className="dk-v-views">
            <i className="pi pi-eye" />
            <span>{fmtViews(viewCount)} {t.views}</span>
          </div>
          <div className="dk-v-title">{title}</div>
          <Caption caption={detail && detail.caption} t={t} />
        </div>

        {active && videoUrl && !errored && (
          <div className="dk-v-ctrl">
            <div className="dk-v-ctrl-row">
              <span className="dk-v-pp" onClick={togglePlay}>
                <i className={"pi " + (playing ? "pi-pause" : "pi-play")} />
              </span>
              <span className="dk-v-time">{fmtTime(time)} / {fmtTime(duration)}</span>
              <div className="dk-v-volwrap">
                <span className="dk-v-mute" onClick={onToggleMute}>
                  <i className={"pi " + (muted ? "pi-volume-off" : "pi-volume-up")} />
                </span>
                <input
                  type="range"
                  className="dk-v-vol"
                  min="0"
                  max="100"
                  value={muted ? 0 : volume}
                  onChange={(e) => onVolume(+e.target.value)}
                />
              </div>
            </div>
            <div className="dk-v-prog" ref={progRef} onPointerDown={(e) => { draggingRef.current = true; seekAt(e.clientX); }}>
              <div className="dk-v-prog-fill" style={{ width: frac * 100 + "%" }} />
              <div className="dk-v-prog-knob" style={{ left: frac * 100 + "%" }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function ShortsFeed({ t, startId, seed, loginUrl }) {
  const [items, setItems] = useState([]);
  const [details, setDetails] = useState({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(80);
  const [showLogin, setShowLogin] = useState(false);
  const [ready, setReady] = useState(false);

  const detailCache = useRef({});
  const viewed = useRef(new Set());
  const loadingRef = useRef(false);
  const pageRef = useRef(1);
  const totalRef = useRef(0);
  const liveSeedRef = useRef(seed);
  const cyclingRef = useRef(false);
  const containerRef = useRef(null);
  const itemRefs = useRef([]);
  const startIndexRef = useRef(0);

  const isLoggedIn = !!AuthenticationService.instance().getCurrentUser();

  const cacheDetail = useCallback((publicId, d) => {
    detailCache.current[publicId] = d;
    setDetails((prev) => ({ ...prev, [publicId]: d }));
  }, []);

  const ensureDetail = useCallback(
    (publicId) => {
      if (!publicId || detailCache.current[publicId]) return;
      getShortDetail(publicId)
        .then((d) => cacheDetail(publicId, d))
        .catch(() => {});
    },
    [cacheDetail]
  );

  const refreshDetail = useCallback(
    (publicId) => {
      if (!publicId) return Promise.resolve();
      return getShortDetail(publicId).then((d) => cacheDetail(publicId, d));
    },
    [cacheDetail]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getShorts(1, PAGE_SIZE, seed);
        if (cancelled) return;
        let list = res.items || [];
        totalRef.current = res.total || list.length;
        pageRef.current = 1;

        let idx = startId ? list.findIndex((s) => s.publicId === startId) : -1;
        if (startId && idx < 0) {
          try {
            const d = await getShortDetail(startId);
            if (cancelled) return;
            cacheDetail(startId, d);
            list = [toListItem(d), ...list.filter((s) => s.publicId !== startId)];
            idx = 0;
          } catch (e) {
            idx = 0;
          }
        }
        startIndexRef.current = Math.max(0, idx);
        setItems(list);
        setActiveIndex(startIndexRef.current);
        setReady(true);
      } catch (e) {
        setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [seed, startId, cacheDetail]);

  useEffect(() => {
    if (!ready) return;
    const el = itemRefs.current[startIndexRef.current];
    if (el && containerRef.current) containerRef.current.scrollTop = el.offsetTop;
  }, [ready]);

  const loadMore = useCallback(() => {
    if (loadingRef.current) return;

    if (items.length >= totalRef.current && totalRef.current > 0) {
      liveSeedRef.current = Math.floor(Math.random() * 2147483647);
      pageRef.current = 0;
      totalRef.current = 0;
      cyclingRef.current = true;
    }

    loadingRef.current = true;
    const next = pageRef.current + 1;
    getShorts(next, PAGE_SIZE, liveSeedRef.current)
      .then((res) => {
        setItems((prev) => {
          if (cyclingRef.current) {
            cyclingRef.current = false;
            return [...prev, ...(res.items || [])];
          }
          const seen = new Set(prev.map((s) => s.publicId));
          return [...prev, ...(res.items || []).filter((s) => !seen.has(s.publicId))];
        });
        totalRef.current = res.total || totalRef.current;
        pageRef.current = next;
      })
      .catch(() => {})
      .finally(() => {
        loadingRef.current = false;
      });
  }, [items.length, seed]);

  useEffect(() => {
    const current = items[activeIndex];
    if (!current) return;
    try {
      window.history.replaceState(null, "", `?shortsId=${current.publicId}`);
    } catch (e) {}
    ensureDetail(current.publicId);
    if (activeIndex >= items.length - 2) loadMore();
  }, [activeIndex, items, ensureDetail, loadMore]);

  useEffect(() => {
    const root = containerRef.current;
    if (!root || !items.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio >= 0.6) {
            const idx = Number(e.target.dataset.index);
            if (!Number.isNaN(idx)) setActiveIndex(idx);
          }
        });
      },
      { root, threshold: [0.6] }
    );
    itemRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [items.length]);

  const onView = (publicId) => {
    if (!viewed.current.has(publicId)) {
      viewed.current.add(publicId);
      incrementShortView(publicId);
    }
  };

  const onLike = async (publicId) => {
    if (!isLoggedIn) {
      setShowLogin(true);
      return;
    }
    const d = detailCache.current[publicId];
    if (!d) return;
    try {
      const res = d.likedByMe ? await unlikeShort(publicId) : await likeShort(publicId);
      cacheDetail(publicId, { ...d, likedByMe: res.liked, likeCount: res.likeCount });
    } catch (e) {}
  };

  const onShare = async (publicId) => {
    const url = `${location.origin}${location.pathname}?shortsId=${publicId}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch (e) {}
    const toastEl = document.createElement("div");
    toastEl.className = "dk-toast";
    toastEl.textContent = t.copied;
    document.body.appendChild(toastEl);
    setTimeout(() => toastEl.remove(), 2400);
  };

  const toggleMute = () => setMuted((m) => !m);
  const changeVolume = (v) => {
    setVolume(v);
    setMuted(v === 0);
  };

  const exit = () => {
    window.location.href = "/";
  };

  const navigate = (dir) => {
    const el = itemRefs.current[activeIndex + dir];
    if (el && containerRef.current) {
      containerRef.current.scrollTo({ top: el.offsetTop, behavior: "smooth" });
    }
  };

  if (ready && items.length === 0) {
    return (
      <div className="dk-feed dk-feed-empty">
        <button className="dk-feed-back" onClick={exit} aria-label={t.exit}>
          <i className="pi pi-chevron-left" /> {t.exit}
        </button>
        <div className="dk-feed-empty-box">
          <div className="dk-state-emoji">🌰</div>
          <p>{t.empty}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dk-feed" ref={containerRef}>
      {items.map((s, i) => (
        <FeedItem
          key={s.publicId}
          item={s}
          index={i}
          active={i === activeIndex}
          innerRef={(el) => (itemRefs.current[i] = el)}
          detail={details[s.publicId] || detailCache.current[s.publicId]}
          t={t}
          muted={muted}
          volume={volume}
          onToggleMute={toggleMute}
          onMutedSync={setMuted}
          onExit={exit}
          onNavigate={navigate}
          onRefresh={refreshDetail}
          onVolume={changeVolume}
          onLike={onLike}
          onShare={onShare}
          onView={onView}
        />
      ))}

      {showLogin && (
        <div
          className="dk-loginpop"
          onClick={(e) => {
            if (e.target.classList.contains("dk-loginpop")) setShowLogin(false);
          }}
        >
          <div className="dk-loginpop-card">
            <div className="dk-lp-ic"><i className="pi pi-heart" /></div>
            <h4>{t.loginTitle}</h4>
            <p>{t.loginDesc}</p>
            <div className="dk-lp-btns">
              <button className="dk-btn" onClick={() => setShowLogin(false)}>{t.loginLater}</button>
              <button
                className="dk-btn dk-btn-primary"
                onClick={() => (window.location.href = loginUrl || "/auth/login")}
              >
                {t.loginNow}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShortsFeed;
